import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type InsertShipment = Database['public']['Tables']['shipment']['Insert']
export type UpdateShipment = Database['public']['Tables']['shipment']['Update']
export type Shipment_ = Database['public']['Tables']['shipment']['Row']

export type ShipmentWithLocation = Shipment_ & {
  quote_shipping_location?: {
    shipping_locations: {
      name: string | null
      company_name: string | null
    } | null
  } | null
}

export const useShipmentsApi = ({
  quote_id,
}: {
  quote_id?: number | null
}) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: shipments, isPending: isLoading } = useQuery({
    queryKey: [queryKeys.shipments, quote_id],
    queryFn: async () => {
      if (!quote_id) return []
      const { data, error } = await supabase
        .from('shipment')
        .select(
          '*, quote_shipping_location!shipment_quote_shipping_location_id_fkey(shipping_locations(name, company_name))'
        )
        .eq('quote_id', quote_id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as ShipmentWithLocation[]
    },
    enabled: !!quote_id,
  })

  const createShipment = useMutation({
    mutationFn: async (data: InsertShipment) =>
      await supabase
        .from('shipment')
        .insert(data)
        .select('id')
        .single(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.shipments],
      })
      toast({
        variant: 'success',
        title: 'Shipment created',
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to create shipment',
        description: error.message,
      })
    },
  })

  const updateShipment = useMutation({
    mutationFn: async (updateData: UpdateShipment) =>
      await supabase
        .from('shipment')
        .update(updateData)
        .eq('id', updateData.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.shipments],
      })
      toast({
        variant: 'success',
        title: 'Shipment updated',
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to update shipment',
        description: error.message,
      })
    },
  })

  const deleteShipment = useMutation({
    mutationFn: async (id: number) =>
      await supabase.from('shipment').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.shipments],
      })
      toast({
        variant: 'success',
        title: 'Shipment deleted',
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to delete shipment',
        description: error.message,
      })
    },
  })

  return {
    shipments: shipments ?? [],
    isLoading,
    createShipment,
    updateShipment,
    deleteShipment,
  }
}
